import { useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { X, Image as ImageIcon, Info } from "lucide-react"
import Button from "../common/Button.js"
import {
  recomputeParcel,
  toNum,
  chargeAmountForSubmit,
  sumParcelQty,
  calcFreightFromRate,
} from "../../utils/cargoParcelUtils.js"

const HEADER_BG = "bg-buttonBg"

const money = (value) => {
  const n = Number.parseFloat(value)
  if (!Number.isFinite(n)) return "0.00"
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const rateText = (value) => {
  const n = Number.parseFloat(value)
  if (!Number.isFinite(n)) return "0"
  return n.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

const textOrEmpty = (value) => {
  const text = value == null ? "" : String(value).trim()
  return text
}

const paymentTypeLabel = (mode, fallback) => {
  if (mode === "to_pay") return "ToPay"
  if (mode === "paid") return "Paid"
  if (mode === "paid_balance") return "Paid Balance"
  if (mode === "credit") return "Credit"
  const label = textOrEmpty(fallback)
  if (!label) return "ToPay"
  return label.replace(/\s+/g, "")
}

const PartyBox = ({ title, location, hubPhone, name, contact }) => (
  <div className="min-w-0 flex flex-col">
    <div className={`${HEADER_BG} text-white font-bold text-[13px] sm:text-sm px-3 py-2 border-b border-gray-300`}>
      {title}
    </div>
    <div className="px-3 py-2.5 text-[13px] sm:text-sm text-gray-900 leading-snug space-y-0.5 min-h-[92px]">
      <p className="break-words">{textOrEmpty(location) || "—"}</p>
      {textOrEmpty(hubPhone) ? <p>{hubPhone}</p> : null}
      {textOrEmpty(name) ? <p className="uppercase">{name}</p> : null}
      {textOrEmpty(contact) ? <p>{contact}</p> : null}
    </div>
  </div>
)

const SummaryRow = ({ label, value, strong = false, rowClass = "", labelClass = "", valueClass = "" }) => (
  <tr className={rowClass}>
    <td className={`px-3 py-1.5 border border-gray-300 text-[13px] sm:text-sm ${strong ? "font-bold" : ""} ${labelClass}`}>
      {label}
    </td>
    <td
      className={`px-3 py-1.5 border border-gray-300 text-right tabular-nums text-[13px] sm:text-sm w-[38%] sm:w-[32%] ${
        strong ? "font-bold" : ""
      } ${valueClass}`}
    >
      {money(value)}
    </td>
  </tr>
)

const QtyEditCell = ({ value, disabled = false, onCommit }) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(String(value ?? 1))
  const inputRef = useRef(null)
  const skipCommitRef = useRef(false)

  useEffect(() => {
    if (!editing) setDraft(String(value ?? 1))
  }, [value, editing])

  useEffect(() => {
    if (!editing) return undefined
    const el = inputRef.current
    if (!el) return undefined
    el.focus()
    el.select()
    return undefined
  }, [editing])

  const parseQty = (raw) => {
    const parsed = Number.parseInt(String(raw).replace(/\D/g, ""), 10)
    if (Number.isFinite(parsed) && parsed >= 1) return Math.min(parsed, 9999)
    const fallback = Number.parseInt(String(value), 10)
    return Number.isFinite(fallback) && fallback >= 1 ? fallback : 1
  }

  const commit = () => {
    if (skipCommitRef.current) {
      skipCommitRef.current = false
      return
    }
    const next = parseQty(draft)
    setEditing(false)
    if (next !== Number(value)) onCommit?.(next)
  }

  const cancel = () => {
    skipCommitRef.current = true
    setDraft(String(value ?? 1))
    setEditing(false)
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        value={draft}
        disabled={disabled}
        aria-label="Edit quantity"
        onChange={(e) => setDraft(e.target.value.replace(/\D/g, ""))}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault()
            commit()
          } else if (e.key === "Escape") {
            e.preventDefault()
            cancel()
          }
        }}
        className="w-16 ml-auto block text-right px-1 py-0.5 border border-buttonBg rounded text-[13px] sm:text-sm tabular-nums outline-none ring-1 ring-buttonBg bg-white"
      />
    )
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => setEditing(true)}
      title="Click to edit quantity"
      className="w-full text-right tabular-nums cursor-pointer hover:bg-blue-50 rounded px-1 py-0.5 disabled:cursor-not-allowed"
    >
      {value}
    </button>
  )
}

const ShipmentCreatePreviewModal = ({
  open,
  onClose,
  onConfirm,
  loading = false,
  formData,
  senderLocationDisplay,
  receiverLocationDisplay,
  senderHubContact = "",
  receiverHubContact = "",
  parcels = [],
  onQtyChange,
  isCargoMode = false,
  busFreightByWeight = false,
  freightCharges,
  totalAmount,
  balanceAmount,
  gstAmounts = {},
  paymentStatusLabel,
  gcPreview,
  submittedEwayBills = [],
  autoMl = null,
  chargeDefaults = {},
  mediaFiles = [],
  selectedClient = null,
  useShipmentClient = false,
}) => {
  const mediaItems = useMemo(
    () =>
      !open
        ? []
        : (mediaFiles || []).map((file, index) => ({
            key: `${file.name}-${file.lastModified}-${index}`,
            url: URL.createObjectURL(file),
            name: file.name,
            video:
              file?.type?.startsWith("video/") ||
              /\.(mp4|webm|mov|m4v|3gp)$/i.test(file?.name || ""),
          })),
    [open, mediaFiles],
  )

  useEffect(() => {
    if (!open) return undefined
    const previous = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])

  useEffect(
    () => () => {
      mediaItems.forEach((item) => URL.revokeObjectURL(item.url))
    },
    [mediaItems],
  )

  if (!open) return null

  const parcelCount = sumParcelQty(parcels)
  const scaledCharge = (key, field) =>
    chargeAmountForSubmit(chargeDefaults, key, formData[field], parcelCount)

  const freight = toNum(freightCharges)
  const loadingCharges = chargeDefaults.loading?.enabled
    ? scaledCharge("loading", "loading_charges")
    : 0
  const docketCharges = chargeDefaults.docket?.enabled ? toNum(formData.docket_fee) : 0
  const handlingCharges = chargeDefaults.handling?.enabled ? toNum(formData.handling_charges) : 0
  const deliveryCharges = (chargeDefaults.door_delivery?.enabled || toNum(formData.delivery_charges) > 0)
    ? scaledCharge("door_delivery", "delivery_charges")
    : 0
  const doorPickupCharges = (chargeDefaults.door_pickup?.enabled || toNum(formData.door_pickup_charges) > 0)
    ? scaledCharge("door_pickup", "door_pickup_charges")
    : 0
  const insuranceCharges = chargeDefaults.insurance?.enabled ? toNum(formData.insurance_charges) : 0
  const tollCharges = chargeDefaults.toll?.enabled ? toNum(formData.toll_charges) : 0
  const fuelCharges = chargeDefaults.fuel_surcharge?.enabled ? toNum(formData.fuel_surcharge) : 0
  const packingCharges = chargeDefaults.packing?.enabled ? toNum(formData.packing_charges) : 0
  const otherCharges = toNum(formData.other_charges)
  const discount = toNum(formData.discount)
  const gstTotal = toNum(gstAmounts.sgst) + toNum(gstAmounts.cgst) + toNum(gstAmounts.igst)
  const paidAmount = toNum(formData.paid_amount)
  const netAmount = toNum(totalAmount)
  const amountBalance = toNum(balanceAmount)
  const byWeight = isCargoMode || busFreightByWeight
  const uomLabel = byWeight ? "KG" : "1 Fixed"
  const ratePerKg = toNum(formData.rate_per_kg)

  const extraChargeRows = [
    { label: "L Charges", value: loadingCharges, show: true },
    { label: "Docket Charges", value: docketCharges, show: true },
    { label: "Handling", value: handlingCharges, show: handlingCharges > 0 },
    { label: "Door Delivery", value: deliveryCharges, show: deliveryCharges > 0 },
    { label: "Door Pickup", value: doorPickupCharges, show: doorPickupCharges > 0 },
    { label: "Insurance", value: insuranceCharges, show: insuranceCharges > 0 },
    { label: "Toll", value: tollCharges, show: tollCharges > 0 },
    { label: "Fuel Surcharge", value: fuelCharges, show: fuelCharges > 0 },
    { label: "Packing", value: packingCharges, show: packingCharges > 0 },
    { label: "Other Charges", value: otherCharges, show: otherCharges > 0 },
    { label: "Discount", value: discount, show: discount > 0 },
  ].filter((row) => row.show)

  const chargesSubTotal = Math.max(
    0,
    Math.round(
      freight +
        loadingCharges +
        handlingCharges +
        deliveryCharges +
        doorPickupCharges +
        docketCharges +
        insuranceCharges +
        tollCharges +
        fuelCharges +
        packingCharges +
        otherCharges -
        discount,
    ),
  )

  const packageRows = (parcels || []).map((row, index) => {
    const computed = isCargoMode ? recomputeParcel(row) : row
    const qty = Math.max(toNum(row.qty, 1), 1)
    const chargeable = toNum(computed.chargeable_weight || row.actual_weight)
    const actual = toNum(row.actual_weight)
    let rate = toNum(row.article_amount)
    let lineFreight = rate * qty
    if (isCargoMode) {
      rate = ratePerKg
      lineFreight = calcFreightFromRate(ratePerKg, chargeable)
    } else if (busFreightByWeight) {
      rate = ratePerKg
      lineFreight = calcFreightFromRate(ratePerKg, actual)
    }
    return {
      index: index + 1,
      parcelIndex: index,
      type: textOrEmpty(row.package_name || row.article_type),
      qty,
      rate,
      lineFreight,
    }
  })

  const itemsSubTotal = packageRows.reduce((sum, row) => sum + toNum(row.lineFreight), 0)
  const remarks = textOrEmpty(formData.parcel_detail)

  const handleBackdrop = (event) => {
    if (loading) return
    if (event.target === event.currentTarget) onClose?.()
  }

  const handleConfirm = (event) => {
    event?.preventDefault?.()
    event?.stopPropagation?.()
    if (loading) return
    onConfirm?.(event)
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[200] bg-black/50 flex items-stretch sm:items-center justify-center sm:p-3 lg:p-4"
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Confirm shipment"
    >
      <div
        className="bg-white w-full max-w-[720px] h-full sm:h-auto sm:max-h-[94dvh] sm:rounded-md shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-gray-200 shrink-0">
          <div className="min-w-0">
            <h3 className="text-sm sm:text-base font-semibold text-gray-800">Confirm Shipment</h3>
            {textOrEmpty(gcPreview) ? (
              <p className="text-[11px] text-gray-500 truncate">{gcPreview}</p>
            ) : null}
          </div>
          <button
            type="button"
            disabled={loading}
            onClick={onClose}
            className="p-1.5 rounded text-gray-500 hover:text-gray-800 hover:bg-gray-100 shrink-0 disabled:opacity-50"
            aria-label="Close confirmation"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 sm:p-4 bg-white">
          <div className="border border-gray-300">
            <div className="grid grid-cols-2">
              <div className="border-r border-gray-300 min-w-0">
                <PartyBox
                  title="From (Origin)"
                  location={senderLocationDisplay}
                  hubPhone={senderHubContact}
                  name={formData.sender_name}
                  contact={formData.sender_contact}
                />
              </div>
              <div className="min-w-0">
                <PartyBox
                  title="To (Destination)"
                  location={receiverLocationDisplay}
                  hubPhone={receiverHubContact}
                  name={formData.receiver_name}
                  contact={formData.receiver_contact}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 border-t border-gray-300 text-[13px] sm:text-sm">
              <div className="px-3 py-2 border-r border-gray-300 text-gray-800">Payment Type</div>
              <div className="px-3 py-2 font-medium text-gray-900">
                {paymentTypeLabel(formData.mode_of_payment, paymentStatusLabel)}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse min-w-0 sm:min-w-[520px]">
                <thead>
                  <tr className={`${HEADER_BG} text-white text-[12px] sm:text-[13px]`}>
                    <th className="border border-gray-300 px-2 py-2 font-bold text-left whitespace-nowrap">S No</th>
                    <th className="border border-gray-300 px-2 py-2 font-bold text-left">Packaging Type</th>
                    <th className="border border-gray-300 px-2 py-2 font-bold text-left whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        UOM
                        <span title="Fixed = per article. KG = charged by weight.">
                          <Info className="w-3.5 h-3.5 text-white/90" aria-hidden="true" />
                        </span>
                      </span>
                    </th>
                    <th className="border border-gray-300 px-2 py-2 font-bold text-right">Quantity</th>
                    <th className="border border-gray-300 px-2 py-2 font-bold text-right">Rate</th>
                    <th className="border border-gray-300 px-2 py-2 font-bold text-right">Freight</th>
                  </tr>
                </thead>
                <tbody>
                  {packageRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="border border-gray-300 px-2 py-3 text-center text-gray-500 text-sm">
                        No packages added
                      </td>
                    </tr>
                  ) : (
                    packageRows.map((row) => (
                      <tr key={row.index} className="text-[13px] sm:text-sm text-gray-900">
                        <td className="border border-gray-300 px-2 py-2">{row.index}</td>
                        <td className="border border-gray-300 px-2 py-2">{row.type}</td>
                        <td className="border border-gray-300 px-2 py-2">{uomLabel}</td>
                        <td className="border border-gray-300 px-2 py-2 text-right">
                          <QtyEditCell
                            value={row.qty}
                            disabled={loading || !onQtyChange}
                            onCommit={(nextQty) => onQtyChange(row.parcelIndex, nextQty)}
                          />
                        </td>
                        <td className="border border-gray-300 px-2 py-2 text-right tabular-nums">{rateText(row.rate)}</td>
                        <td className="border border-gray-300 px-2 py-2 text-right tabular-nums">{money(row.lineFreight)}</td>
                      </tr>
                    ))
                  )}
                  <tr className="text-[13px] sm:text-sm">
                    <td colSpan={4} className="border border-gray-300 px-2 py-2" />
                    <td className="border border-gray-300 px-2 py-2 text-right font-bold">Sub Total</td>
                    <td className="border border-gray-300 px-2 py-2 text-right tabular-nums font-semibold">
                      {money(itemsSubTotal || freight)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <table className="w-full border-collapse">
              <tbody>
                <SummaryRow label="Freight" value={freight} />
                {extraChargeRows.map((row) => (
                  <SummaryRow key={row.label} label={row.label} value={row.value} />
                ))}
                <SummaryRow label="Sub Total" value={Math.max(0, chargesSubTotal)} strong />
                <SummaryRow label="GST Amount" value={gstTotal} />
                <SummaryRow
                  label="Net Amount"
                  value={netAmount}
                  strong
                  labelClass="bg-gray-500 text-white border-gray-400"
                  valueClass="bg-gray-500 text-white border-gray-400"
                />
                <SummaryRow label="Amount Paid" value={paidAmount} />
                <SummaryRow
                  label="Amount Balance"
                  value={amountBalance}
                  strong
                  valueClass="bg-red-700 text-white border-red-800"
                />
              </tbody>
            </table>

            <div className="border-t border-gray-300 px-3 py-2.5 text-[13px] sm:text-sm">
              <span className="font-bold text-gray-800">Remarks :</span>
              <span className="ml-1 text-gray-800 break-words">{remarks}</span>
            </div>
          </div>

          {useShipmentClient && selectedClient ? (
            <p className="mt-2 text-[12px] text-gray-600">
              Credit Client:{" "}
              <span className="font-medium text-gray-800">
                {selectedClient.full_name || ""}
                {selectedClient.company_name ? ` (${selectedClient.company_name})` : ""}
              </span>
            </p>
          ) : null}

          {submittedEwayBills.length ? (
            <p className="mt-2 text-[12px] text-gray-600">
              E-Way Bills: <span className="font-medium text-gray-800">{submittedEwayBills.join(", ")}</span>
            </p>
          ) : null}

          {autoMl?.enabled ? (
            <p className="mt-2 text-[12px] text-gray-600">
              Auto ML: {autoMl.mode === "existing" ? "Existing ML" : "New ML"}
              {autoMl.vehicleLabel ? ` · ${autoMl.vehicleLabel}` : ""}
              {autoMl.agentLabel ? ` · ${autoMl.agentLabel}` : ""}
              {autoMl.mlSheetId ? ` · ${autoMl.mlSheetId}` : ""}
            </p>
          ) : null}

          <div className="mt-3">
            {mediaItems.length === 0 ? null : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {mediaItems.map((item) => (
                  <div
                    key={item.key}
                    className="relative aspect-square rounded overflow-hidden border border-gray-300 bg-gray-100"
                  >
                    {item.video ? (
                      <video src={item.url} className="w-full h-full object-cover" muted playsInline controls />
                    ) : (
                      <img src={item.url} alt={item.name} className="w-full h-full object-cover" />
                    )}
                    <span className="absolute bottom-0 inset-x-0 bg-black/55 text-white text-[9px] px-1 py-0.5 truncate">
                      {item.video ? "Video" : "Photo"}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {mediaItems.length === 0 && (formData.parcel_image_1 || formData.parcel_image_2) ? (
              <div className="flex items-center gap-2 text-[12px] text-gray-500">
                <ImageIcon className="w-3.5 h-3.5" />
                Parcel images attached
              </div>
            ) : null}
          </div>
        </div>

        <div className="relative z-20 flex items-center justify-end gap-2 px-3 py-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))] border-t border-gray-200 bg-white shrink-0">
          <Button
            variant="primary"
            type="button"
            onClick={onClose}
            disabled={loading}
            className="!bg-blue-700 !border-blue-700 hover:!brightness-110 flex-1 sm:flex-none min-w-[96px] min-h-[44px] touch-manipulation max-sm:active:scale-100"
          >
            Modify
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={handleConfirm}
            loading={loading}
            disabled={loading}
            className="flex-1 sm:flex-none min-w-[96px] min-h-[44px] touch-manipulation max-sm:active:scale-100"
          >
            {loading ? "Creating..." : "Confirm"}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default ShipmentCreatePreviewModal
